import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';

/**
 * A modal component providing a calendar and time-slot picker for scheduling campaign start date and time.
 * It renders a monthly calendar, generates half-hour time slots, enforces a minimum selectable date, and emits the chosen date/time.
 * Handles auxiliary UI state such as loading, an off-days confirmation modal, and pending updates.
 * @component
 * @selector app-campaign-date-picker
 * @example
 *   <app-campaign-date-picker
 *     [isVisible]="true"
 *     [selectedDateTime]="existingDate"
 *     [minDate]="earliestAllowedDate"
 *     [title]="'Start date & time'"
 *     (dateTimeSelected)="onDateSelected($event)"
 *     (closeModal)="onClosePicker()">
 *   </app-campaign-date-picker>
 * @input boolean isVisible - Controls whether the date picker modal is visible.
 * @input Date | null selectedDateTime - The currently selected Date/time; component initializes its selection from this value when provided.
 * @input Date | null minDate - The earliest selectable date/time; dates/times before this are disabled.
 * @input string title - Title text displayed in the modal (defaults to "Start date & time").
 * @output EventEmitter<void> closeModal - Emitted when the modal is requested to close (e.g., cancel or after successful update).
 * @output EventEmitter<Date> dateTimeSelected - Emitted with the finalized Date when the user confirms a selection.
 *
 * Key methods:
 *  - ngOnInit(): Initializes time slots and generates the calendar for the current month.
 *  - ngOnChanges(changes): Syncs internal selection when selectedDateTime input changes.
 *  - initializeTimeSlots(): Builds 30-minute time slots for a full day and prepares display strings.
 *  - generateCalendar(): Produces a 6-week (42-day) calendar matrix for the currentMonth.
 *  - formatTime(date): Formats a Date into a 12-hour display string (e.g., "1:00 PM").
 *  - previousMonth(), nextMonth(): Navigate the calendar month and regenerate calendarDays.
 *  - onDateSelect(date), onTimeSelect(slot): Handlers to pick date and time and trigger selection updates.
 *  - updateSelectedDateTime(): Combines selected date and time into a Date, validates against minDate, and updates selectedDateTime.
 *  - isSelectedDate(date), isToday(date): Helpers to determine UI selection and today highlighting.
 *  - isTimeSlotSelected(slot), isTimeSlotDisabled(slot), isDateDisabled(date): Availability checks for rendering and preventing invalid picks.
 *  - onClose(), onUpdate(): Modal actions to emit closeModal and dateTimeSelected after validation.
 *  - onModalContentClick(event): Stops propagation to avoid closing when clicking inside modal content.
 *  - Off-days modal helpers: onCloseOffDaysModal(), onChooseAnotherDay(), onProceedAnyway() to manage confirmation flows.
 *
 * Important lifecycle hooks:
 *  - ngOnInit: Called once to prepare time slots and initial calendar state.
 *  - ngOnChanges: Reacts to changes in @Input selectedDateTime to keep internal selectedDate and selectedTime in sync.
 */
@Component({
  selector: 'app-campaign-date-picker',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatButtonModule],
  templateUrl: './campaign-date-picker.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./campaign-date-picker.component.scss'],
})
export class CampaignDatePickerComponent implements OnInit, OnChanges {
  @Input() isVisible = false;
  @Input() selectedDateTime: Date | null = null;
  @Input() minDate: Date | null = null;
  @Input() title = 'Start date & time';
  @Output() closeModal = new EventEmitter<void>();
  @Output() dateTimeSelected = new EventEmitter<Date>();

  loading = false;

  // Off-days modal properties
  showOffDaysModal = false;
  pendingUpdate = false;

  // Calendar properties
  weekDays: string[] = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  calendarDays: any[] = [];
  currentMonth: Date = new Date();
  selectedDate: Date | null = null;
  selectedTime = '';

  // Time slots for the date picker
  timeSlots: any[] = [];

  ngOnInit(): void {
    this.initializeTimeSlots();
    this.generateCalendar();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedDateTime'] && this.selectedDateTime) {
      this.selectedDate = new Date(this.selectedDateTime);
      this.selectedTime = this.formatTime(this.selectedDateTime);
    }
  }

  /**
   * Initialize the component's timeSlots array with 30-minute interval Date entries for every hour of a 24-hour day.
   * @example
   * nitializeTimeSlots()
   * // this.timeSlots will be populated like:
   * // [{ time: Date, displayTime: '00:00', available: true }, { time: Date, displayTime: '00:30', available: true }, ...]
   * @returns {void} Updates the instance's timeSlots property; no value is returned.
   **/
  private readonly BUSINESS_START_HOUR = 9; // 9:00 AM
  private readonly BUSINESS_END_HOUR = 18; // 6:00 PM

  initializeTimeSlots(): void {
    this.timeSlots = [];
    for (let hour = this.BUSINESS_START_HOUR; hour <= this.BUSINESS_END_HOUR; hour += 1) {
      const minuteSteps = hour === this.BUSINESS_END_HOUR ? [0] : [0, 30];
      for (const minute of minuteSteps) {
        const time = new Date();
        time.setHours(hour, minute, 0, 0);
        this.timeSlots.push({
          time: time,
          displayTime: this.formatTime(time),
          available: true,
        });
      }
    }
  }

  formatTime(date: Date): string {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  /**
   * Generates a 6-week calendar grid (42 days) for the component based on this.currentMonth and populates this.calendarDays.
   * @example
   * generateCalendar()
   * // this.calendarDays -> [{ date: Date, dayNumber: number, isCurrentMonth: boolean, isOffDay: boolean }, ...] (42 items)
   * @returns {void} Populates this.calendarDays on the component; does not return a value.
   **/
  generateCalendar(): void {
    const year = this.currentMonth.getFullYear();
    const month = this.currentMonth.getMonth();

    // Get first day of month and calculate starting point
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    this.calendarDays = [];

    // Generate 42 days (6 weeks)
    for (let i = 0; i < 42; i += 1) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);

      this.calendarDays.push({
        date: new Date(date),
        dayNumber: date.getDate(),
        isCurrentMonth: date.getMonth() === month,
        isOffDay: false, // You can add logic here to check for off days
      });
    }
  }

  getCurrentMonthYear(): string {
    return this.currentMonth.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
    });
  }

  previousMonth(): void {
    this.currentMonth = new Date(
      this.currentMonth.getFullYear(),
      this.currentMonth.getMonth() - 1,
      1
    );
    this.generateCalendar();
  }

  nextMonth(): void {
    this.currentMonth = new Date(
      this.currentMonth.getFullYear(),
      this.currentMonth.getMonth() + 1,
      1
    );
    this.generateCalendar();
  }

  onDateSelect(date: Date): void {
    if (this.isDateDisabled(date)) {
      return;
    }

    this.selectedDate = new Date(date);
    this.updateSelectedDateTime();
  }

  onTimeSelect(slot: any): void {
    this.selectedTime = slot.displayTime;
    this.updateSelectedDateTime();
  }

  /**
   * Combine selectedDate and selectedTime (12-hour "HH:MM AM/PM") into this.selectedDateTime and validate against minDate; if the resulting datetime is before minDate, reset selectedTime and selectedDateTime.
   * @example
   * this.pdateSelectedDateTime()
   * void
   * @returns {void} Updates component state (this.selectedDateTime / this.selectedTime); does not return a value.
   **/
  updateSelectedDateTime(): void {
    if (this.selectedDate && this.selectedTime) {
      const [time, period] = this.selectedTime.split(' ');
      const [hours, minutes] = time.split(':').map(Number);

      let hour24 = hours;
      if (period === 'PM' && hours !== 12) {
        hour24 += 12;
      } else if (period === 'AM' && hours === 12) {
        hour24 = 0;
      }

      const newDateTime = new Date(this.selectedDate);
      newDateTime.setHours(hour24, minutes, 0, 0);

      // Validate against minDate
      if (this.minDate && newDateTime < this.minDate) {
        // Reset selected time if it's before minDate
        this.selectedTime = '';
        this.selectedDateTime = null;
        return;
      }

      this.selectedDateTime = newDateTime;
    }
  }

  isSelectedDate(date: Date): boolean {
    if (!this.selectedDate) return false;
    return date.toDateString() === this.selectedDate.toDateString();
  }

  isToday(date: Date): boolean {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }

  isTimeSlotSelected(slot: any): boolean {
    return slot.displayTime === this.selectedTime;
  }

  /**
   * Determine whether a given time slot should be disabled based on the component's selectedDate and minDate.
   * @example
   * sTimeSlotDisabled({time: new Date(2026, 2, 17, 9, 30)})
   * false
   * @param {{any}} {{slot}} - The time slot object; expected to have a Date-like 'time' property whose hours and minutes are used for comparison.
   * @returns {{boolean}} Whether the provided time slot is disabled.
   **/
  isTimeSlotDisabled(slot: any): boolean {
    const h = slot.time.getHours();
    const m = slot.time.getMinutes();
    if (
      h < this.BUSINESS_START_HOUR ||
      h > this.BUSINESS_END_HOUR ||
      (h === this.BUSINESS_END_HOUR && m > 0)
    ) {
      return true;
    }

    if (!this.selectedDate || !this.minDate) {
      return false;
    }

    // Check if the selected date is today (same day as minDate)
    const selectedDateOnly = new Date(this.selectedDate);
    selectedDateOnly.setHours(0, 0, 0, 0);

    const minDateOnly = new Date(this.minDate);
    minDateOnly.setHours(0, 0, 0, 0);

    // If selected date is before minDate, disable all slots
    if (selectedDateOnly < minDateOnly) {
      return true;
    }

    // If selected date is today, check if the time slot is in the past
    if (selectedDateOnly.getTime() === minDateOnly.getTime()) {
      const slotDateTime = new Date(this.selectedDate);
      const slotHours = slot.time.getHours();
      const slotMinutes = slot.time.getMinutes();
      slotDateTime.setHours(slotHours, slotMinutes, 0, 0);
      return slotDateTime < this.minDate;
    }

    return false;
  }

  /**
   * Return true if the given date is before the component's minDate (compares only date portion, ignoring time).
   * @example
   * sDateDisabled(new Date(2020, 0, 1))
   * true
   * @param {{Date}} {{date}} - Date to check for being disabled relative to the component's minDate.
   * @returns {{boolean}} Returns true when the provided date is strictly earlier than minDate (date-only comparison), otherwise false.
   **/
  isDateDisabled(date: Date): boolean {
    if (!this.minDate) {
      return false;
    }

    const dateOnly = new Date(date);
    dateOnly.setHours(0, 0, 0, 0);

    const minDateOnly = new Date(this.minDate);
    minDateOnly.setHours(0, 0, 0, 0);

    return dateOnly < minDateOnly;
  }

  onClose(): void {
    this.closeModal.emit();
  }

  onModalContentClick(event: Event): void {
    event.stopPropagation();
  }

  onUpdate(): void {
    if (this.selectedDateTime) {
      // Final validation before emitting
      if (this.minDate && this.selectedDateTime < this.minDate) {
        return;
      }
      this.dateTimeSelected.emit(this.selectedDateTime);
      this.closeModal.emit();
    }
  }

  onCloseOffDaysModal(): void {
    this.showOffDaysModal = false;
    this.pendingUpdate = false;
  }

  onChooseAnotherDay(): void {
    this.showOffDaysModal = false;
  }

  onProceedAnyway(): void {
    this.showOffDaysModal = false;
    this.onUpdate();
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
